// src/pages/product/PartnerProductDetail.tsx

import { useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Divider,
  Button,
  Stack,
} from '@mui/material';
import { ArrowBackIosNew } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import { RequiredDataType } from '@models/product';
import {
  PartnerProductResponse,
  ProductSaleType,
  ProductType,
} from '@apis/product';
import { usePartnerProductDetail } from '@queries/products/usePartnerProductDetail';

import {
  PRODUCT_SALE_TYPE_LABELS,
  PRODUCT_TYPE_LABELS,
  getLabel,
} from '@utils/productLabels';

import { usePageHeader } from '@components/common/pageHeader.context';

type Country = {
  countryId: number;
  englishName: string;
};

/**
 * PartnerProductDetail
 *
 * Partner-facing read-only view of a single product.
 *
 * IMPORTANT (Header standard):
 * - Main title/description/breadcrumbs are defined via route handle.pageHeader in App.tsx.
 * - This page injects ONLY the leading slot:
 *   - title: product name
 *   - description: product code
 * - This page does NOT inject header actions.
 * - Legacy inline header UI ("Back to products" + product title/code) has been removed
 *   to avoid duplication with the global AppPageHeader.
 *
 * Responsibilities:
 * - Fetch partner product detail
 * - Show:
 *   - Basic info (code / name / description)
 *   - Specification (validity, data, FUP, refresh, voice, coverage)
 *   - Order / product type and required customer fields
 */
export const PartnerProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const { data } = usePartnerProductDetail(Number(productId));

  // PageHeader slots
  const { setPageHeaderSlots } = usePageHeader();

  // Fallback when data is not yet loaded (keeps UI stable)
  const productDetail: PartnerProductResponse = data?.data[0] ?? {
    productId: 0,
    productCode: '',
    productSaleType: ProductSaleType.REAL_TIME,
    productType: ProductType.ESIM,
    productName: '',
    description: '',
    specification: {
      dayCount: 0,
      isUnlimitedData: true,
      isVoiceCallSupported: false,
      dataCapacity: {
        quantity: 0,
        unit: 'GB',
        refreshPeriodUnit: 'NEVER',
        refreshPeriodValue: 0,
      },
      isFup: false,
    },
    productCoverages: [],
    requiredData: {
      requiredFields: [],
    },
  };

  const spec = productDetail.specification;
  const requiredFields = productDetail.requiredData?.requiredFields ?? [];

  const coverages: Country[] =
    productDetail.productCoverages?.map((c) => ({
      countryId: c.country.countryId,
      englishName: c.country.englishName,
    })) ?? [];

  const sortedCoverages = [...coverages].sort((a, b) =>
    a.englishName.localeCompare(b.englishName),
  );

  const handleBack = () => {
    navigate(-1);
  };

  // --- Header slot injection (leading only) ---------------------------------
  /**
   * Register page header leading slot with product name/code.
   *
   * - This page does not use header actions.
   * - Root layout resets slots on route changes; we still reset on unmount
   *   to avoid any possibility of stale header content.
   */
  useEffect(() => {
    setPageHeaderSlots({
      leading: {
        title: productDetail.productName || 'Product details',
        description: productDetail.productCode
          ? `Code: ${productDetail.productCode}`
          : undefined,
      },
    });
  }, [
    productDetail.productName,
    productDetail.productCode,
    setPageHeaderSlots,
  ]);

  // Derived display strings for specification
  const validityText = spec.dayCount ? `${spec.dayCount} day(s)` : 'Not specified';

  const dataLimitText = spec.isUnlimitedData
    ? 'Unlimited data'
    : spec.dataCapacity.quantity
    ? `${spec.dataCapacity.quantity} ${spec.dataCapacity.unit}`
    : 'No data limit set';

  const afterLimitText = spec.isUnlimitedData
    ? 'Not applicable (unlimited)'
    : spec.isFup
    ? 'Apply FUP (reduced speed)'
    : 'Cut off data';

  const refreshText =
    spec.isUnlimitedData || spec.dataCapacity.refreshPeriodUnit === 'NEVER'
      ? 'No refresh (fixed quota)'
      : `Every ${spec.dataCapacity.refreshPeriodValue} ${spec.dataCapacity.refreshPeriodUnit.toLowerCase()}`;

  const voiceText = spec.isVoiceCallSupported
    ? 'Voice calls included'
    : 'Data only (no voice calls)';

  const saleTypeLabel = getLabel(
    PRODUCT_SALE_TYPE_LABELS,
    productDetail.productSaleType,
  );
  const productTypeLabel = getLabel(
    PRODUCT_TYPE_LABELS,
    productDetail.productType,
  );

  const isRequired = (field: RequiredDataType) => requiredFields.includes(field);

  return (
    <Box width="100%">
      {/* 1) General / basic information ------------------------------------- */}
      <Card
        elevation={0}
        sx={(theme) => ({
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
        })}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              General
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Basic product details such as code, name and description.
            </Typography>
          }
        />
        <CardContent>
          <Stack spacing={1.5}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Product Code
              </Typography>
              <Typography variant="body1">{productDetail.productCode || '-'}</Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Product Name
              </Typography>
              <Typography variant="body1">{productDetail.productName || '-'}</Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Description
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {productDetail.description || '-'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* 2) Specification (validity / data / refresh / voice / coverage) ---- */}
      <Card
        elevation={0}
        sx={(theme) => ({
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
        })}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              Specification
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Validity, data limit, refresh cycle, voice support and country coverage.
            </Typography>
          }
        />
        <CardContent>
          <Stack spacing={2.5}>
            {/* Validity */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Validity
              </Typography>
              <Typography variant="body1">{validityText}</Typography>
            </Box>

            {/* Data / FUP / After limit */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Data
              </Typography>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                {dataLimitText}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                After limit: {afterLimitText}
              </Typography>
            </Box>

            {/* Refresh cycle */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Refresh cycle
              </Typography>
              <Typography variant="body1">{refreshText}</Typography>
            </Box>

            {/* Voice call support */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Voice calls
              </Typography>
              <Typography variant="body1">{voiceText}</Typography>
            </Box>

            <Divider />

            {/* Coverage */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Coverage
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  columnGap: 1,
                  rowGap: 1.2,
                }}
              >
                {sortedCoverages.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No countries configured.
                  </Typography>
                )}
                {sortedCoverages.map((c) => (
                  <Chip
                    key={c.countryId}
                    label={c.englishName}
                    size="small"
                    sx={{ maxWidth: 220 }}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* 3) Order / product type & required customer fields ----------------- */}
      <Card
        elevation={0}
        sx={(theme) => ({
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
        })}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={700}>
              Settings & required fields
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary">
              Order type, product type and information that must be collected from customers.
            </Typography>
          }
        />
        <CardContent>
          <Stack spacing={2.5}>
            {/* Order Type */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Order type
              </Typography>
              <Box mt={0.5}>
                <Chip
                  label={saleTypeLabel}
                  size="small"
                  variant={
                    productDetail.productSaleType === ProductSaleType.REAL_TIME
                      ? 'filled'
                      : 'outlined'
                  }
                />
              </Box>
            </Box>

            {/* Product Type */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Product type
              </Typography>
              <Box mt={0.5}>
                <Chip label={productTypeLabel} size="small" variant="outlined" />
              </Box>
            </Box>

            <Divider />

            {/* Required customer fields */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase' }}
              >
                Required customer fields
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  columnGap: 1,
                  rowGap: 1,
                }}
              >
                {/* Activation date */}
                {isRequired(RequiredDataType.ACTIVATION_DATE) && (
                  <Chip label="Activation date" size="small" />
                )}
                {/* We intentionally do NOT use VALIDITY_DAYS as agreed in admin UI */}

                {isRequired(RequiredDataType.IMEI) && <Chip label="IMEI" size="small" />}
                {isRequired(RequiredDataType.EID) && <Chip label="EID" size="small" />}
                {isRequired(RequiredDataType.CUSTOMER_NAME) && (
                  <Chip label="User name" size="small" />
                )}
                {isRequired(RequiredDataType.PASSPORT_DATA) && (
                  <Chip label="Passport" size="small" />
                )}

                {requiredFields.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No additional information required.
                  </Typography>
                )}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Bottom: keep only the existing back action (non-header UI) --------- */}
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIosNew fontSize="small" />}
        >
          Back
        </Button>
      </Box>
    </Box>
  );
};
